/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Bell, X, PartyPopper } from "lucide-react";
import { useLang } from "./i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * User Notification Bell — for stockers, NMs, Managers.
 * Turns red + pulses when unread > 0. Click opens drawer.
 * Celebrates on new 🎉 rewards (confetti burst + emoji rain).
 */
const NotificationBell = ({ userId }) => {
  const { t } = useLang();
  const [data, setData] = useState({ count: 0, unread: 0, notifications: [] });
  const [open, setOpen] = useState(false);
  const [celebrateFor, setCelebrateFor] = useState(null); // notification id
  const [seenCelebrations, setSeenCelebrations] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("vy_seen_celebrations") || "[]")); } catch { return new Set(); }
  });
  const drawerRef = useRef(null);

  const persistSeen = (set) => {
    try { localStorage.setItem("vy_seen_celebrations", JSON.stringify([...set])); } catch {}
  };

  const load = async () => {
    if (!userId) return;
    try {
      const r = await axios.get(`${API}/user/${userId}/notifications`);
      setData(r.data);
      // detect NEW celebration (one we haven't shown yet)
      const celebration = (r.data.notifications || []).find(n => n.celebration && !seenCelebrations.has(n.id));
      if (celebration) {
        setCelebrateFor(celebration);
        const next = new Set(seenCelebrations); next.add(celebration.id);
        setSeenCelebrations(next); persistSeen(next);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const tm = setInterval(load, 30000);
    return () => clearInterval(tm);
  }, [userId]);

  useEffect(() => {
    const onDoc = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target) && !e.target.closest("[data-testid='notif-bell-btn']")) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/user/${userId}/notifications/${id}/read`);
      load();
    } catch (_) {}
  };
  const markAllRead = async () => {
    try {
      await axios.post(`${API}/user/${userId}/notifications/mark-all-read`);
      load();
    } catch (_) {}
  };

  const hasUnread = data.unread > 0;

  return (
    <div className="relative">
      <button
        onClick={() => { const next = !open; setOpen(next); if (next) { load(); /* auto mark all read when opening */ if (hasUnread) markAllRead(); } }}
        className={`relative p-2 rounded-full transition-all ${hasUnread ? "bg-red-600 hover:bg-red-500 animate-pulse shadow-[0_0_18px_rgba(239,68,68,0.7)]" : "bg-zinc-800 hover:bg-zinc-700"}`}
        data-testid="notif-bell-btn"
        aria-label="Notifications"
        title={hasUnread ? `${data.unread} ${t("notifications_title")}` : t("notif_none")}
      >
        <Bell className={`w-5 h-5 ${hasUnread ? "text-white" : "text-zinc-400"}`} />
        {hasUnread && (
          <span className="absolute -top-1 -right-1 bg-yellow-400 text-red-900 text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center" data-testid="notif-unread-count">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={drawerRef}
          className="absolute right-0 mt-2 w-80 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50"
          data-testid="notif-drawer"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <p className="text-white font-bold text-sm flex items-center gap-2"><Bell className="w-4 h-4 text-yellow-400" /> {t("notifications_title")} ({data.count})</p>
            <div className="flex gap-1">
              {data.unread > 0 && (
                <button onClick={markAllRead} className="text-zinc-400 hover:text-white text-[10px] px-2 py-1 rounded bg-zinc-800" data-testid="notif-mark-all-read">
                  {t("notif_all_read")}
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.notifications.length === 0 ? (
              <p className="text-zinc-500 text-xs text-center py-8">{t("notif_none")}</p>
            ) : (
              data.notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => !n.read && markRead(n.id)}
                  className={`px-4 py-3 border-b border-zinc-800 cursor-pointer hover:bg-zinc-800/60 transition-colors ${n.celebration ? "bg-gradient-to-br from-fuchsia-900/40 via-amber-900/30 to-emerald-900/40 border-l-4 border-l-amber-400" : !n.read ? "bg-violet-950/20" : ""}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold ${n.celebration ? "text-amber-200" : (!n.read ? "text-white" : "text-zinc-400")}`}>
                        {n.celebration && <PartyPopper className="w-4 h-4 inline mr-1 text-amber-300" />}
                        {n.title}
                        {n.bonus_amount && <span className="ml-2 bg-emerald-500 text-black text-[10px] font-black px-2 py-0.5 rounded-full">+${n.bonus_amount}</span>}
                      </p>
                      <p className={`text-xs mt-0.5 whitespace-pre-wrap ${n.celebration ? "text-amber-100" : "text-zinc-500"}`}>{n.message}</p>
                      <p className="text-[10px] text-zinc-600 mt-1">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
                    </div>
                    {!n.read && <span className="w-2 h-2 bg-violet-500 rounded-full mt-1.5 flex-shrink-0" />}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* 🎉 CELEBRATION OVERLAY — confetti + emoji rain when a reward notification arrives */}
      {celebrateFor && (
        <div
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="celebration-overlay"
          onClick={() => setCelebrateFor(null)}
        >
          {/* emoji rain */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 36 }).map((_, i) => {
              const emoji = ["🎉","🎊","🥳","🎈","🎁","💰","⭐","✨"][i % 8];
              const left = Math.random() * 100;
              const delay = Math.random() * 2;
              const dur = 2.5 + Math.random() * 2;
              const size = 24 + Math.random() * 28;
              return (
                <span key={i}
                  className="absolute animate-emoji-fall"
                  style={{
                    left: `${left}%`,
                    top: `-10%`,
                    fontSize: `${size}px`,
                    animationDelay: `${delay}s`,
                    animationDuration: `${dur}s`,
                  }}>{emoji}</span>
              );
            })}
          </div>
          <div className="relative bg-gradient-to-br from-fuchsia-950 via-amber-950 to-emerald-950 border-4 border-amber-400 rounded-3xl p-8 max-w-md w-full text-center shadow-[0_0_80px_rgba(251,191,36,0.6)] animate-bounce-in">
            <PartyPopper className="w-16 h-16 text-amber-300 mx-auto mb-3 drop-shadow-[0_0_12px_rgba(251,191,36,0.9)]" />
            <h3 className="text-white font-black text-2xl mb-2">{celebrateFor.title}</h3>
            <p className="text-amber-100 text-sm mb-4 whitespace-pre-wrap">{celebrateFor.message}</p>
            {celebrateFor.bonus_amount && (
              <div className="bg-black/60 border-2 border-emerald-400 rounded-2xl py-4 mb-4">
                <p className="text-emerald-400 text-xs uppercase tracking-widest">Rekonpans</p>
                <p className="text-white font-black text-5xl mt-1">+${celebrateFor.bonus_amount}</p>
              </div>
            )}
            <button
              onClick={() => setCelebrateFor(null)}
              data-testid="celebration-close"
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black py-3 rounded-xl uppercase text-sm tracking-widest"
            >Mèsi!</button>
          </div>
          <style>{`
            @keyframes emoji-fall {
              0% { transform: translateY(0) rotate(0deg); opacity: 1; }
              100% { transform: translateY(110vh) rotate(720deg); opacity: 0.2; }
            }
            .animate-emoji-fall { animation-name: emoji-fall; animation-timing-function: linear; animation-iteration-count: 1; animation-fill-mode: forwards; }
            @keyframes bounce-in {
              0% { transform: scale(0.4); opacity: 0; }
              60% { transform: scale(1.08); opacity: 1; }
              100% { transform: scale(1); }
            }
            .animate-bounce-in { animation: bounce-in 0.6s ease-out; }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
